import { useEffect, useState, type JSX, type ReactNode } from 'react';
import { cn } from '../lib/cn.js';
import { useIaSettingsPort, type LiaRecordStatus } from '../lib/iaSettings.js';
import { IconArrowRight, IconModule } from '../icons/index.js';
import { liaRecordCards } from './liaRecordCards.js';
import type { RecordLink, ToolStep } from './types.js';

/** L'etat de chaque fiche citee (archivee, supprimee...), demande au port des reglages IA. */
function useRecordStatuses(records: readonly RecordLink[]): ReadonlyMap<string, LiaRecordStatus> {
  const settings = useIaSettingsPort();
  const [statuses, setStatuses] = useState<ReadonlyMap<string, LiaRecordStatus>>(new Map());
  const wanted = records.filter((record) => record.family).map((record) => `${record.family}:${record.id}`).join('|');
  useEffect(() => {
    const check = settings?.recordStatus;
    if (!check || !wanted) return;
    let cancelled = false;
    void Promise.all(records.filter((record) => record.family).map(async (record) =>
      [record.id, await check(record.family!, record.id)] as const)).then((entries) => {
      if (!cancelled) setStatuses(new Map(entries));
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted, settings?.recordStatus]);
  return statuses;
}

export interface CartesDeFichesLiaProps {
  /** Les etapes d'outils du tour (absentes d'un sujet relu : seules `records` comptent). */
  steps?: readonly ToolStep[];
  /** Les fiches gardees avec le tour. */
  records?: readonly RecordLink[];
  /** Le texte de la reponse : une fiche seulement lue n'a sa carte que si la reponse la cite. */
  answer?: string;
  /** L'icone de l'app d'une fiche (celle du dock) ; repli : l'icone module. */
  iconeDApp?: (appId: string) => ReactNode;
  onOpenRecord: (record: RecordLink) => void;
}

/**
 * LES FICHES LUES, CREEES OU MODIFIEES PAR UN TOUR DE LIA : une carte ouvrable
 * chacune, avec titre et provenance sur deux lignes. LE MEME rendu dans la fenetre de
 * communication et dans Canaux, qui relit les sujets (retour R4 du fondateur).
 */
export function CartesDeFichesLia({ steps = [], records: additional = [], answer, iconeDApp, onOpenRecord }: CartesDeFichesLiaProps): JSX.Element | null {
  const cards = liaRecordCards(steps, additional, answer);
  const statuses = useRecordStatuses(cards.map((card) => card.record));
  if (cards.length === 0) return null;
  const sameTitle = (title: string) => cards.filter((card) => card.record.title.trim().toLocaleLowerCase('fr') === title.trim().toLocaleLowerCase('fr')).length > 1;
  return <div className="mt-1 flex max-w-full flex-col items-start gap-1">{cards.map(({ record, step, action }) => {
    const doorName = record.doorName ?? (step ? /(?:^|,) porte (.+)$/.exec(step.label)?.[1] : undefined);
    const status = statuses.get(record.id);
    const gone = status === 'deleted' || status === 'inaccessible' || status === 'missing';
    const verb = status === 'deleted' ? 'supprimée' : status === 'inaccessible' ? 'inaccessible' : status === 'missing' ? 'introuvable'
      : action === 'create' ? 'créée' : action === 'update' ? 'modifiée' : 'ouvrir';
    const extra = [status === 'archived' ? 'archivée' : null, step?.parent ? `avec « ${step.parent.title} »` : null,
      record.hint && sameTitle(record.title) ? record.hint : null].filter(Boolean).join(' · ');
    const detail = [doorName ?? 'Fiche', verb !== 'ouvrir' ? verb : null, extra || null].filter(Boolean).join(' · ');
    const body = <>
      <span aria-hidden="true" className={cn('flex h-8 w-8 shrink-0 items-center justify-center text-ink-500 [&>svg]:h-5 [&>svg]:w-5', gone && 'grayscale')}>{iconeDApp?.(record.appId) ?? <IconModule size={20} />}</span>
      <span className="min-w-0 flex-1">
        <span className={cn('block break-words text-[0.8125rem] font-semibold leading-5 text-ink-900', gone && 'text-ink-500 line-through decoration-ink-300')}>{record.title}</span>
        <span className="mt-0.5 block text-[0.6875rem] leading-4 text-ink-500">{detail}</span>
      </span>
      {!gone && <IconArrowRight size={16} aria-hidden="true" className="shrink-0 text-ink-500" />}
    </>;
    return gone
      ? <div key={record.id} aria-disabled="true" data-record-status={status} aria-label={`${record.title}, fiche ${verb}`}
        className="taki-bulle taki-bulle-pointe flex w-64 max-w-full items-center gap-3 text-left opacity-80">{body}</div>
      : <button key={record.id} type="button" onClick={() => onOpenRecord(record)} aria-label={`Ouvrir la fiche ${record.title}`} data-record-status={status}
        className="taki-bulle taki-bulle-pointe flex w-64 max-w-full items-center gap-3 text-left transition-colors hover:bg-border focus-visible:shadow-ring">{body}</button>;
  })}</div>;
}
